import React, { useEffect, useState, useCallback } from 'react';
import { Chart, Axis, Geom, Tooltip, Coord } from 'bizcharts';
import { Card, Input, Select, Pagination, Checkbox, Radio, message, Avatar, Tooltip as AntTooltip, Dropdown, Menu } from 'antd';
import AppSpinner from './shared/AppSpinner';
import { SearchOutlined, FilterOutlined, SettingOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTaskExport } from '../hooks/useTaskExport';
import moment from 'moment';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { debounce } from 'lodash';
import { DEFAULT_STATUSES, urgencyHexColors } from '../configs/task.config';
import { accent, text, fontSize as fontSizeTokens, fontWeight as fontWeightTokens } from '../configs/designTokens';
import useResponsiveTask from '../hooks/useResponsiveTask';
import TaskDrawerFull from './TaskDrawerFull';
import AvatarS3 from '../../../shared/images/AvatarS3';
import '../styles/index.scss';

const { Option } = Select;

const TaskOverdueChart = () => {
  const { fetchTasksOverdue, currentUser, tasksOverdueLoading, overdueTasks } = useTaskContext();
  const { exportTasks, isExporting } = useTaskExport();

  const chartRef = React.useRef(null);
  const today = moment();
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [userFilter, setUserFilter] = useState('all');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [visibleStatuses, setVisibleStatuses] = useState({
    '1–3 days late': true,
    '4–7 days late': true,
    '>7 days late': true,
  });

  // Urgency-semantic colors matching design system
  const statusColors = {
    '1–3 days late': urgencyHexColors.MEDIUM,
    '4–7 days late': urgencyHexColors.HIGH,
    '>7 days late': urgencyHexColors.CRITICAL,
  };

  const buildOverdueWhere = useCallback(() => ({
    AND: [
      { status_in: DEFAULT_STATUSES },
      {
        OR: [
          {
            createdBy: {
              roles_some: {
                org: {
                  OR: [
                    {
                      parentOrg_some: {
                        id:
                          currentUser &&
                          currentUser.role &&
                          currentUser.role.org &&
                          currentUser.role.org.id,
                      },
                    },
                    {
                      id:
                        currentUser &&
                        currentUser.role &&
                        currentUser.role.org &&
                        currentUser.role.org.id,
                    },
                  ],
                },
              },
            },
          },
        ],
      },
      {
        dueDate_lt: new Date().toISOString(),
      },
      { dueDate_not: null },
    ],
  }), [currentUser]);

  const handleFetchTasksOverdue = useCallback(
    debounce(async () => {
      try {
        await fetchTasksOverdue({
          variables: {
            where: buildOverdueWhere(),
            orderBy: 'updatedAt_DESC',
            first: 1000,
            skip: 0,
          },
        });
      } catch (error) {
        message.error('Error fetching overdue tasks:', error);
      }
    }, 500),
    [fetchTasksOverdue, currentUser, buildOverdueWhere]
  );

  useEffect(() => {
    if (currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.id) {
      handleFetchTasksOverdue();
    }
  }, [currentUser, handleFetchTasksOverdue]);

  const isUserInvolvedInTask = (task) => {
    if (!currentUser || !currentUser.id) return false;

    const isRequestor = task.createdBy && task.createdBy.id === currentUser.id;

    const isAssignee = task.assigneeUser && task.assigneeUser.id === currentUser.id;

    const isAssignees =
      task.assigneeUsers && task.assigneeUsers.some((assignee) => assignee.id === currentUser.id);

    const isFollower =
      task.followers && task.followers.some((follower) => follower.id === currentUser.id);

    const isCollaborator =
      task.collaborators &&
      task.collaborators.some((collaborator) => collaborator.id === currentUser.id);
    return isRequestor || isAssignee || isAssignees || isFollower || isCollaborator;
  };

  const transformedData = overdueTasks
    .map((task) => {
      if (!task.dueDate) return null;

      const dueDate = moment(task.dueDate);
      const diff = today.diff(dueDate, 'day');

      if (diff <= 0) return null;

      let status;
      if (diff <= 3) status = '1–3 days late';
      else if (diff <= 7) status = '4–7 days late';
      else status = '>7 days late';

      return {
        task: task.id,
        taskName: task.name || 'Task ' + task.id,
        overdue: diff,
        status: status,
        id: task.id,
        originalTask: task,
        assigneeUser: task.assigneeUser,
        assigneeUsers: task.assigneeUsers,
      };
    })
    .filter((item) => item !== null);

  // Get unique assignees for filter
  const uniqueAssignees = React.useMemo(() => {
    const assigneeMap = new Map();
    transformedData.forEach((item) => {
      const assignee = item.assigneeUser || (item.assigneeUsers && item.assigneeUsers[0]);
      if (assignee && assignee.id) {
        assigneeMap.set(assignee.id, {
          id: assignee.id,
          name: assignee.name || assignee.username || 'Unknown',
        });
      }
    });
    return Array.from(assigneeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [transformedData]);

  const filteredData = transformedData.filter((item) => {
    const matchesSearch = item.taskName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const isVisible = visibleStatuses[item.status];

    let matchesUserFilter = true;
    if (userFilter === 'myTasks') {
      matchesUserFilter = isUserInvolvedInTask(item.originalTask);
    }

    let matchesAssigneeFilter = true;
    if (assigneeFilter.length > 0) {
      const assignee = item.assigneeUser || (item.assigneeUsers && item.assigneeUsers[0]);
      const assigneeId = assignee ? assignee.id : null;
      matchesAssigneeFilter = assigneeFilter.includes(assigneeId);
    }

    return matchesSearch && matchesStatus && isVisible && matchesUserFilter && matchesAssigneeFilter;
  });

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  const toggleLegendItem = (status) => {
    setVisibleStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
    setCurrentPage(1);
  };

  // Handle user filter change
  const handleUserFilterChange = (e) => {
    setUserFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
    setDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedTaskId(null);
  };

  const handleTaskUpdate = () => {
    handleFetchTasksOverdue();
  };

  // Get severity class for legend color boxes
  const getSeverityClass = (status) => {
    if (status === '>7 days late') return 'overdue-chart__color-box--critical';
    if (status === '4–7 days late') return 'overdue-chart__color-box--high';
    return 'overdue-chart__color-box--medium';
  };

  // Count tasks by severity for legend badges
  const getStatusCount = (status) => {
    return filteredData.filter(item => item.status === status).length;
  };

  const CustomLegend = () => (
    <div className="overdue-chart__legend">
      {Object.entries(statusColors)
        .filter(([status]) => status !== 'On Time')
        .reverse() // Show critical first
        .map(([status, color]) => (
          <div
            key={status}
            className={`overdue-chart__legend-item ${!visibleStatuses[status] ? 'overdue-chart__legend-item--disabled' : ''}`}
            onClick={() => toggleLegendItem(status)}
          >
            <Checkbox
              checked={visibleStatuses[status]}
              onChange={() => toggleLegendItem(status)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className={`overdue-chart__color-box ${getSeverityClass(status)}`} />
            <span className="overdue-chart__legend-label">{status}</span>
            <span className="overdue-chart__legend-count">{getStatusCount(status)}</span>
          </div>
        ))}
    </div>
  );

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.off('interval:click');
        chartRef.current.off('interval:mouseenter');
        chartRef.current.off('interval:mouseleave');
      }
    };
  }, []);

  if (tasksOverdueLoading) {
    return (
      <Card title="Overdue Tasks" className="overdue-chart__card">
        <div className="overdue-chart__loading">
          <AppSpinner size="large" />
        </div>
      </Card>
    );
  }

  if (!transformedData.length) {
    return (
      <Card title="Overdue Tasks" className="overdue-chart__card">
        <div className="overdue-chart__empty">
          <div className="overdue-chart__empty-title">No overdue tasks</div>
          <div className="overdue-chart__empty-text">
            All tasks are on track. Great job!
          </div>
        </div>
      </Card>
    );
  }

  const renderAvatarColumn = () => {
    const chartHeight = isMobile
      ? Math.max(400, paginatedData.length * 35 + 150)
      : Math.max(600, paginatedData.length * 40 + 200);
    const topPadding = isMobile ? 40 : 60;
    const bottomPadding = isMobile ? 60 : 80;
    const availableHeight = chartHeight - topPadding - bottomPadding;
    const itemHeight = availableHeight / paginatedData.length;
    const avatarSize = isMobile ? 20 : 24;

    return (
      <div
        className="overdue-chart__avatar-col"
        style={{
          height: chartHeight,
          paddingTop: topPadding,
          paddingBottom: bottomPadding,
        }}
      >
        <div className="overdue-chart__avatar-header">
          Assignee
        </div>
        {[...paginatedData].reverse().map((item) => {
          const assignee = item.assigneeUser || (item.assigneeUsers && item.assigneeUsers[0]);

          return (
            <div
              key={item.id}
              className="overdue-chart__avatar-item"
              style={{ height: itemHeight }}
            >
              {assignee ? (
                <AntTooltip title={assignee.name || assignee.username || 'Unknown'}>
                  <span>
                    <AvatarS3
                      imgKey={assignee.avatar && assignee.avatar.key}
                      username={assignee.name || assignee.username || '?'}
                      avatarProps={{ size: avatarSize }}
                    />
                  </span>
                </AntTooltip>
              ) : (
                <AntTooltip title="Unassigned">
                  <span>
                    <Avatar size={avatarSize} className="overdue-chart__avatar-unassigned">
                      ?
                    </Avatar>
                  </span>
                </AntTooltip>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render detailed chart
  const renderChart = () => {
    const taskNameMap = paginatedData.reduce((acc, item) => {
      acc[item.task] = item.taskName;
      return acc;
    }, {});

    return (
      <div
        className="overdue-chart__wrapper"
        onContextMenu={(e) => {
          if (hoveredTaskId) {
            e.preventDefault();
            window.open(`/tasks/details/${hoveredTaskId}`, '_blank');
          }
        }}
      >
        {!isMobile && renderAvatarColumn()}
        <div className="overdue-chart__content">
          <Chart
        autoFit
        forceFit
        width="100%"
        height={isMobile ? Math.max(400, paginatedData.length * 35 + 150) : Math.max(600, paginatedData.length * 40 + 200)}
        data={paginatedData}
        scale={{
          task: {
            type: 'cat',
          },
          overdue: { min: 0, nice: true },
        }}
        padding={isMobile ? [40, 20, 60, 130] : [60, 80, 80, 200]}
        onGetG2Instance={(chart) => {
          chartRef.current = chart;
          chartRef.current.off('interval:click');
          chartRef.current.off('interval:mouseenter');
          chartRef.current.off('interval:mouseleave');

          chartRef.current.on('interval:mouseenter', (ev) => {
            if (ev && ev.data && ev.data._origin && ev.data._origin.id) {
              const taskId = ev.data._origin.id;
              setHoveredTaskId(taskId);
            }
          });

          chartRef.current.on('interval:mouseleave', () => {
            setHoveredTaskId(null);
          });

          // Left-click handler
          chartRef.current.on('interval:click', (ev) => {
            if (ev && ev.data && ev.data._origin && ev.data._origin.id) {
              const taskId = ev.data._origin.id;
              handleTaskClick(taskId);
            }
          });
        }}
      >
        <Coord transpose />
        <Axis
          name="task"
          title={null}
          label={{
            style: { fontSize: isMobile ? 10 : 11, textAlign: 'end', fill: text.label },
            formatter: (taskId) => {
              const taskName = taskNameMap[taskId] || taskId;
              const maxLength = isMobile ? 15 : 28;
              if (taskName.length > maxLength) {
                return taskName.substring(0, maxLength) + '...';
              }
              return taskName;
            },
          }}
        />
        <Axis
          name="overdue"
          title={{ offset: isMobile ? 30 : 40, style: { fontSize: isMobile ? 11 : 12, fill: text.label } }}
          label={{
            style: { fontSize: isMobile ? 10 : 11, fill: text.muted },
            formatter: (val) => isMobile ? `${val}d` : `${val} days overdue`,
          }}
        />
        <Tooltip showTitle={false} shared showCrosshairs />
        <Geom
          type="interval"
          position="task*overdue"
          color={[
            'status',
            (status) => {
              return statusColors[status] || accent.primary;
            },
          ]}
          size={Math.max(8, Math.min(25, 500 / paginatedData.length))}
          tooltip={[
            'taskName*overdue*status',
            (taskName, overdue, status) => {
              return {
                name: '',
                value: `<strong>${taskName}</strong><br/>${overdue} days overdue`,
              };
            },
          ]}
        />
      </Chart>
        </div>
      </div>
    );
  };

  // Calculate summary stats
  const criticalCount = filteredData.filter(item => item.status === '>7 days late').length;
  const highCount = filteredData.filter(item => item.status === '4–7 days late').length;
  const mediumCount = filteredData.filter(item => item.status === '1–3 days late').length;

  return (
    <Card
      title={`Overdue Tasks (${filteredData.length})`}
      extra={criticalCount > 0 && (
        <span style={{ color: urgencyHexColors.CRITICAL, fontSize: fontSizeTokens.meta, fontWeight: fontWeightTokens.medium }}>
          {criticalCount} Critical
        </span>
      )}
      className="overdue-chart__card"
    >
      <div className="overdue-chart__header">
        {/* Mobile: Search + Filter button row */}
        {isMobile && (
          <div className="overdue-chart__mobile-search-row">
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="overdue-chart__search"
              allowClear
              prefix={<SearchOutlined style={{ color: text.muted }} />}
            />
            <Dropdown
              overlay={
                <Menu className="task-board-utilities-menu">
                  <Menu.Item
                    key="filter"
                    icon={<FilterOutlined />}
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                  >
                    {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
                  </Menu.Item>
                  <Menu.Item
                    key="export"
                    icon={<DownloadOutlined />}
                    onClick={() => exportTasks(buildOverdueWhere())}
                    disabled={isExporting}
                  >
                    {isExporting ? 'Exporting...' : 'Export Tasks'}
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
              placement="bottomRight"
            >
              <button
                className={`overdue-chart__filter-btn ${showMobileFilters ? 'overdue-chart__filter-btn--active' : ''}`}
              >
                <SettingOutlined />
                {(statusFilter !== 'all' || assigneeFilter.length > 0 || userFilter !== 'all') && (
                  <span className="overdue-chart__filter-badge" />
                )}
              </button>
            </Dropdown>
          </div>
        )}

        {/* Mobile: Collapsible filters */}
        {isMobile && showMobileFilters && (
          <div className="overdue-chart__mobile-filters">
            <Select
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              className="overdue-chart__filter-item overdue-chart__status-filter"
            >
              <Option value="all">All Severity</Option>
              <Option value=">7 days late">Critical (&gt;7 days)</Option>
              <Option value="4–7 days late">High (4–7 days)</Option>
              <Option value="1–3 days late">Medium (1–3 days)</Option>
            </Select>
            <Select
              mode="multiple"
              placeholder="Filter by Assignee"
              value={assigneeFilter}
              onChange={(value) => {
                setAssigneeFilter(value);
                setCurrentPage(1);
              }}
              className="overdue-chart__filter-item overdue-chart__assignee-filter"
              allowClear
              maxTagCount={1}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {uniqueAssignees.map((assignee) => (
                <Option key={assignee.id} value={assignee.id}>
                  {assignee.name}
                </Option>
              ))}
            </Select>
            <Radio.Group
              value={userFilter}
              onChange={handleUserFilterChange}
              buttonStyle="solid"
              size="middle"
              className="overdue-chart__user-filter-mobile"
            >
              <Radio.Button value="all">All Tasks</Radio.Button>
              <Radio.Button value="myTasks">My Tasks</Radio.Button>
            </Radio.Group>
          </div>
        )}

        {/* Desktop: All filters in a row */}
        {!isMobile && (
          <>
            <div className="overdue-chart__filters">
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="overdue-chart__filter-item overdue-chart__search"
                allowClear
                prefix={<SearchOutlined style={{ color: text.muted }} />}
              />
              <Select
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value);
                  setCurrentPage(1);
                }}
                className="overdue-chart__filter-item overdue-chart__status-filter"
              >
                <Option value="all">All Severity</Option>
                <Option value=">7 days late">Critical (&gt;7 days)</Option>
                <Option value="4–7 days late">High (4–7 days)</Option>
                <Option value="1–3 days late">Medium (1–3 days)</Option>
              </Select>
              <Select
                mode="multiple"
                placeholder="Filter by Assignee"
                value={assigneeFilter}
                onChange={(value) => {
                  setAssigneeFilter(value);
                  setCurrentPage(1);
                }}
                className="overdue-chart__filter-item overdue-chart__assignee-filter"
                allowClear
                maxTagCount={1}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {uniqueAssignees.map((assignee) => (
                  <Option key={assignee.id} value={assignee.id}>
                    {assignee.name}
                  </Option>
                ))}
              </Select>
              <Select
                value={pageSize}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                className="overdue-chart__filter-item overdue-chart__page-size-filter"
              >
                <Option value={10}>10 / page</Option>
                <Option value={20}>20 / page</Option>
                <Option value={50}>50 / page</Option>
                <Option value={100}>100 / page</Option>
              </Select>
            </div>

            <div className="overdue-chart__user-filter">
              <Radio.Group
                value={userFilter}
                onChange={handleUserFilterChange}
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="all">All Tasks</Radio.Button>
                <Radio.Button value="myTasks">My Tasks</Radio.Button>
              </Radio.Group>
            </div>

            <Dropdown
              overlay={
                <Menu className="task-board-utilities-menu">
                  <Menu.Item
                    key="export"
                    icon={<DownloadOutlined />}
                    onClick={() => exportTasks(buildOverdueWhere())}
                    disabled={isExporting}
                  >
                    {isExporting ? 'Exporting...' : 'Export Tasks'}
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
              placement="bottomRight"
            >
              <button className="overdue-chart__settings-btn">
                <SettingOutlined />
              </button>
            </Dropdown>
          </>
        )}
      </div>

      <CustomLegend />
      {renderChart()}

      {filteredData.length > pageSize && (
        <div className="overdue-chart__pagination">
          <Pagination
            current={currentPage}
            total={filteredData.length}
            pageSize={pageSize}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} tasks`}
            simple={windowWidth < 768}
            size={windowWidth < 768 ? 'small' : 'default'}
          />
        </div>
      )}

      <TaskDrawerFull
        visible={drawerVisible}
        onClose={handleDrawerClose}
        taskId={selectedTaskId}
        onTaskUpdate={handleTaskUpdate}
      />
    </Card>
  );
};

export default TaskOverdueChart;
